import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Users,
    Package,
    Layers,
    Palette,
    LogOut,
    Menu,
    X,
    LayoutDashboard,
    Box,
    Shapes,
    Maximize2,
    Circle,
    Type
} from 'lucide-react';

const Sidebar = ({ isOpen, setIsOpen, isMobile }) => {
    const navigate = useNavigate();

    const handleLogout = () => {
        localStorage.removeItem('plasa_admin_session');
        navigate('/admin');
    };

    const navItems = [
        { name: 'Resumen', path: '/admin/dashboard', icon: LayoutDashboard, exact: true },
        { name: 'Productos', path: '/admin/dashboard/productos', icon: Package },
    ];

    const catalogs = [
        { name: 'Base de Productos', path: '/admin/dashboard/base-productos', icon: Package },
        { name: 'Categorías', path: '/admin/dashboard/categorias', icon: Layers },
        { name: 'Colores', path: '/admin/dashboard/colores', icon: Palette },
        { name: 'Materiales', path: '/admin/dashboard/materiales', icon: Box },
        { name: 'Texturas', path: '/admin/dashboard/texturas', icon: Shapes },
        { name: 'Presentaciones', path: '/admin/dashboard/presentaciones', icon: Maximize2 },
        { name: 'Bases', path: '/admin/dashboard/bases', icon: Layers },
        { name: 'Diámetros', path: '/admin/dashboard/diametros', icon: Circle },
        { name: 'Diseñada Para', path: '/admin/dashboard/disenada_para', icon: Type },
    ];

    return (
        <motion.aside
            initial={false}
            animate={{
                width: isOpen ? 250 : (isMobile ? 0 : 80),
                x: isMobile && !isOpen ? -250 : 0
            }}
            className={`bg-white dark:bg-zinc-950 border-r border-gray-200 dark:border-zinc-900 h-screen flex flex-col transition-all duration-300 fixed lg:relative z-[60] text-zinc-900 dark:text-white shadow-xl dark:shadow-none ${isMobile && !isOpen ? 'pointer-events-none' : 'pointer-events-auto'}`}
        >
            {/* Header */}
            <div className="h-20 flex items-center justify-between px-6 border-b border-gray-200 dark:border-zinc-900/50 bg-gray-50/50 dark:bg-black/20 transition-colors">
                {isOpen ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <span className="font-black text-xl tracking-tighter text-zinc-900 dark:text-white">PLASA<span className="text-red-600">.ADMIN</span></span>
                    </motion.div>
                ) : (
                    <span className="font-black text-xl text-red-600">P.</span>
                )}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                >
                    {isOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1">
                {navItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.exact}
                        className={({ isActive }) => `
              flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 group
              ${isActive
                                ? 'bg-red-50 dark:bg-red-600/10 text-red-600 dark:text-red-500 font-bold border-r-2 border-red-600'
                                : 'text-zinc-600 dark:text-zinc-500 hover:bg-gray-100 dark:hover:bg-zinc-900 hover:text-zinc-900 dark:hover:text-zinc-100'}
            `}
                    >
                        <item.icon size={20} className="min-w-[20px]" />
                        {isOpen && (
                            <motion.span
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="whitespace-nowrap"
                            >
                                {item.name}
                            </motion.span>
                        )}
                    </NavLink>
                ))}

                <div className="my-6 border-t border-gray-200 dark:border-zinc-900/50 mx-2 transition-colors"></div>
                {isOpen && <div className="px-3 text-xs font-bold text-zinc-500 dark:text-zinc-600 uppercase tracking-widest mb-2">Catálogos</div>}

                {catalogs.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) => `
              flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm group
              ${isActive
                                ? 'bg-gray-100 dark:bg-zinc-900 text-red-600 dark:text-red-400 font-medium'
                                : 'text-zinc-500 dark:text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-gray-100 dark:hover:bg-zinc-900/50'}
            `}
                    >
                        <item.icon size={18} className="min-w-[18px]" />
                        {isOpen && (
                            <motion.span
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="whitespace-nowrap"
                            >
                                {item.name}
                            </motion.span>
                        )}
                    </NavLink>
                ))}
            </nav>

            {/* Footer / User */}
            <div className="p-4 border-t border-gray-200 dark:border-zinc-900 bg-gray-50/50 dark:bg-black/20 transition-colors">
                <div className={`flex items-center ${isOpen ? 'gap-3' : 'justify-center'}`}>
                    <div className="w-8 h-8 rounded-full bg-white dark:bg-zinc-800 flex items-center justify-center ring-2 ring-white dark:ring-zinc-900 overflow-hidden shadow-sm">
                        <img src="/images/home/logo.png" alt="Logo" className="w-6 h-6 object-contain" />
                    </div>
                    {isOpen && (
                        <div className="flex-1 overflow-hidden">
                            <p className="text-sm font-bold truncate text-zinc-900 dark:text-white">Administrador</p>
                            <p className="text-xs text-zinc-500 truncate">Online</p>
                        </div>
                    )}
                    {isOpen && (
                        <button
                            onClick={handleLogout}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-950 text-zinc-400 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-500 rounded-lg transition-colors"
                            title="Cerrar Sesión"
                        >
                            <LogOut size={18} />
                        </button>
                    )}
                </div>
            </div>
        </motion.aside>
    );
};

export default Sidebar;
